import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import gsap from 'gsap';
import { CustomEase } from 'gsap/CustomEase';
import { CustomWiggle } from 'gsap/CustomWiggle';
import { DrawSVGPlugin } from 'gsap/DrawSVGPlugin';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { isNil } from 'lodash-es';
import {
  Observable,
  combineLatest,
  debounceTime,
  filter,
  first,
  forkJoin,
  map,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';

import { RoutingHistoryService } from '@aw/shared/services/routing-history';
import { isEmptyString } from '@aw/shared/utilities';

import {
  ENV,
  Environment,
  GoogleTagManagerScript,
} from '@aw/prypco/environment';
import { BusinessUnit } from '@aw/prypco/enums';
import { BlocksService } from '@aw/prypco/modules/blocks/services/blocks';
import { PromptUpdateService } from '@aw/prypco/services/prompt-update';
import {
  AmplitudeService,
  GtmService,
  IntercomService,
  ScriptManagerService,
  initHotJar,
  trackingAllowedRoles,
} from '@aw/prypco/services/script-manager';
import { SeoService } from '@aw/prypco/services/seo';
import { AccountState, UserInfo, UserService } from '@aw/prypco/services/user';
import { UtmService } from '@aw/prypco/services/utm';
import { AuthFacade } from '@aw/prypco/state/auth';
import { SplashScreenFacade } from '@aw/prypco/state/splash-screen';

@UntilDestroy()
@Component({
  selector: 'po-root',
  templateUrl: './app.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, AfterViewInit {
  private readonly scriptManagerService = inject(ScriptManagerService);

  private readonly amplitudeService = inject(AmplitudeService);

  private readonly authFacade = inject(AuthFacade);

  private readonly document = inject(DOCUMENT) as Document;

  private readonly seoService = inject(SeoService);

  private readonly metaService = inject(Meta);

  private readonly promptUpdateService = inject(PromptUpdateService);

  private readonly splashScreenFacade = inject(SplashScreenFacade);

  private readonly routingHistoryService = inject(RoutingHistoryService);

  private readonly utmService = inject(UtmService);

  private readonly env = inject(ENV) as Environment;

  private readonly intercomService = inject(IntercomService);

  private readonly gtmService = inject(GtmService);

  private readonly userService = inject(UserService);

  private readonly blockService = inject(BlocksService);

  private readonly APP_ID = 'e4i69eaq';

  private get splashScreen(): HTMLDivElement {
    return this.document.querySelector('#splashScreen') as HTMLDivElement;
  }

  constructor(readonly router: Router) {
    this.initExternalScripts();
    this.initGsap();
    this.printVersionMessage();
  }

  ngOnInit(): void {
    this.promptUpdateService.init();
    this.routingHistoryService.init();
    this.utmService.init();

    if (this.metaService) {
      this.setMetaTags();
    }
    const { intercom } = this.env.externalScripts;
    if (intercom) {
      this.initIntercom();
    }
  }

  private initGsap(): void {
    gsap.registerPlugin(
      ScrollTrigger,
      ScrollSmoother,
      SplitText,
      DrawSVGPlugin,
      CustomWiggle,
      CustomEase,
    );
  }

  private initExternalScripts() {
    const { googleTagManager, hotjar, amplitude } = this.env.externalScripts;

    const enabledGtmScripts = googleTagManager.filter((s) => s.enabled);
    if (enabledGtmScripts.length) {
      this.initGoogleTagManager(enabledGtmScripts);
      this.initJournifyIdentify();
      this.initBlocksKycStatusTracking();
    }

    if (hotjar.enabled) {
      initHotJar(hotjar);
    }
    if (amplitude.enabled) {
      this.trackAmplitudeIfRolesAllow();
    }
  }

  ngAfterViewInit(): void {
    this.splashScreenFacade.loading$
      .pipe(debounceTime(150), untilDestroyed(this))
      .subscribe({
        next: (isLoading) => {
          if (isLoading) {
            this.showSplashScreen();
          } else {
            this.hideSplashScreen();
          }
        },
      });
  }

  private printVersionMessage(): void {
    const { production: isProduction } = this.env;

    let message = '✨ Application version: 256';

    if (!isProduction) {
      message = message.replace('256', 'Local Development');
    }

    console.log(`%c ${message}`, 'color: #bada55');
  }

  private showSplashScreen(): void {
    const { splashScreen } = this;

    splashScreen.style.display = 'flex';
    splashScreen.style.pointerEvents = 'all';
    splashScreen.style.opacity = '1';
  }

  private hideSplashScreen(): void {
    const { splashScreen } = this;

    splashScreen.addEventListener('transitionend', () => {
      this.deleteSplashScreen(splashScreen);
    });

    splashScreen.addEventListener('transitioncancel', () =>
      this.deleteSplashScreen(splashScreen),
    );

    splashScreen.style.opacity = '0';
  }

  private deleteSplashScreen(splashScreen: HTMLDivElement): void {
    this.document.body.removeChild(splashScreen);
  }

  private setMetaTags(): void {
    const { defaultTitle, defaultDescription } = this.seoService;
    const { noIndex } = this.env;

    this.seoService.setTitle(defaultTitle);
    this.seoService.setDescription(defaultDescription);

    if (noIndex) {
      this.metaService.addTag({ name: 'robots', content: 'noindex' });
    }
  }

  private initGoogleTagManager(scripts: GoogleTagManagerScript[]): void {
    forkJoin(scripts.map((s) => this.scriptManagerService.load(s)))
      .pipe(
        filter((results) => results.some((s) => !isNil(s))),
        tap(() => {
          const dataLayer = ((window as any).dataLayer || []) as Array<{
            'gtm.start': number;
            event: string;
          }>;

          dataLayer.push({
            'gtm.start': new Date().getTime(),
            event: 'gtm.js',
          });
        }),
        first(),
      )
      .subscribe();
  }

  private trackAmplitudeIfRolesAllow(): void {
    this.authFacade.jobTitle$
      .pipe(
        untilDestroyed(this),
        withLatestFrom(this.authFacade.user$),
        map(([jobTitle, userInfo]) => ({
          shouldTrack:
            trackingAllowedRoles.includes(jobTitle) || isEmptyString(jobTitle),
          userId: userInfo,
        })),
        tap(({ shouldTrack }) => {
          this.amplitudeService.shouldTrack = shouldTrack;
        }),
      )
      .subscribe();
  }

  private readonly leadInfo$ = this.fetchUserInfo().pipe(
    switchMap((userInfo: any) => {
      const blocksLeadId = userInfo?.businessUnits?.BLOCKS?.leadId ?? '';
      if (blocksLeadId) {
        return this.blockService.getLead(blocksLeadId);
      }
      return of(null);
    }),
    shareReplay(1),
  );

  private fetchUserInfo(): Observable<UserInfo | null> {
    return this.authFacade.authenticated$.pipe(
      untilDestroyed(this),
      filter((authenticated) => authenticated),
      switchMap(() => this.userService.getUserInfo()),
      first(),
      filter((userInfo): userInfo is UserInfo => !!userInfo),
    );
  }

  private initIntercom() {
    this.leadInfo$.pipe(first()).subscribe((users: any) => {
      if (users) {
        const userData = {
          app_id: this.APP_ID,
          user_id: users.customerId,
          name: `${users.firstName} ${users.lastName}`,
          email: users.email || '',
          created_at: this.toUnixTimestampInSeconds(users.createdDate),
        };

        this.intercomService.boot(userData);
      } else {
        this.intercomService.boot({ app_id: this.APP_ID });
      }

      if (this.isMobile()) {
        this.applyIntercomPadding();
      }
    });
  }

  private initJournifyIdentify() {
    this.leadInfo$.pipe(first()).subscribe((users: any) => {
      if (!users) return;
      this.gtmService.pushEvent({
        event: 'Identify',
        event_label: 'identified',
        user_id: users.customerId,
        email: users.email || '',
        phone: users.phoneNumber || '',
        firstname: users.firstName || '',
        lastname: users.lastName || '',
        timestamp: new Date().toISOString(),
      });
    });
  }

  private initBlocksKycStatusTracking(): void {
    const onboardingCompleted$ = this.authFacade.getAccountState(
      BusinessUnit.Blocks,
      AccountState.OnboardingCompleted,
    );
    const investor$ = this.authFacade.getAccountState(
      BusinessUnit.Blocks,
      AccountState.Investor,
    );
    const rejectedInvestor$ = this.authFacade.getAccountState(
      BusinessUnit.Blocks,
      AccountState.RejectedInvestor,
    );
    const trialInvestorRevoked$ = this.authFacade.getAccountState(
      BusinessUnit.Blocks,
      AccountState.TrialInvestorRevoked,
    );

    // KYC moved to review: onboarding submitted and awaiting verification.
    const inReview$ = combineLatest([
      onboardingCompleted$,
      trialInvestorRevoked$,
      investor$,
      rejectedInvestor$,
    ]).pipe(
      map(
        ([onboardingCompleted, trialInvestorRevoked, isInvestor, isRejected]) =>
          onboardingCompleted &&
          trialInvestorRevoked &&
          !isInvestor &&
          !isRejected,
      ),
    );

    this.trackKycStatusOnce(inReview$, 'kyc_inreview', 'in_review');
    this.trackKycStatusOnce(investor$, 'kyc_approved', 'approved');
    this.trackKycStatusOnce(rejectedInvestor$, 'kyc_rejected', 'rejected');
  }

  private trackKycStatusOnce(
    active$: Observable<boolean>,
    event: string,
    label: string,
  ): void {
    combineLatest([active$, this.authFacade.getLeadId(BusinessUnit.Blocks)])
      .pipe(
        filter(([active]) => active),
        take(1),
        untilDestroyed(this),
      )
      .subscribe(([, leadId]) => {
        this.gtmService.pushEvent(
          {
            event,
            event_label: label,
            user_id: leadId,
            timestamp: new Date().toISOString(),
          },
          false,
        );
      });
  }

  private toUnixTimestampInSeconds(dateStr: string): number {
    const cleanedDateStr = dateStr.slice(0, 23) + 'Z';
    return Math.floor(new Date(cleanedDateStr).getTime() / 1000);
  }

  private isMobile(): boolean {
    return window.innerWidth <= 768;
  }

  private applyIntercomPadding(
    horizontal: number = 20,
    vertical: number = 80,
  ): void {
    const styleTagId = 'custom-intercom-style';
    const existingStyle = document.getElementById(styleTagId);
    if (existingStyle) {
      existingStyle.remove();
    }

    const style = document.createElement('style');
    style.id = styleTagId;
    style.innerHTML = `
        .intercom-lightweight-app-launcher {
          right: ${horizontal}px !important;
          bottom: ${vertical}px !important;
          left: auto !important;
        }
      `;

    const interval = setInterval(() => {
      if (document.querySelector('.intercom-lightweight-app-launcher')) {
        clearInterval(interval);
        document.head.appendChild(style);
      }
    }, 300);
  }
}
